import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import PageTitle from "../../layouts/PageTitle";
import {
  useGetRealEstateClubReportQuery,
  useGetRealEstateTeamsQuery,
} from "../../../store/appApi";
import { selectUserRole } from "../../../store/authSlice";

const panel =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const inputClass =
  "rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

const Kpi = ({ label, value }) => (
  <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-center">
    <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
      {label}
    </div>
    <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
      {value ?? 0}
    </div>
  </div>
);

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "—");

const ClubReport = () => {
  const navigate = useNavigate();
  const isSuperAdmin = useSelector(selectUserRole) === "super-admin";
  const [teamId, setTeamId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [showDaily, setShowDaily] = useState(false);

  const { data: teamData } = useGetRealEstateTeamsQuery({ active: "true" });
  const { data, isFetching } = useGetRealEstateClubReportQuery({
    teamId,
    from,
    to,
  });
  const teams = data?.teams || [];

  const exportExcel = async () => {
    const wb = new ExcelJS.Workbook();
    teams.forEach((t) => {
      const ws = wb.addWorksheet(String(t.name).slice(0, 28) || "Team");
      ws.addRow([`${t.name} — Manager: ${t.manager?.name || ""}`]);
      ws.addRow([`Period: ${data.from} to ${data.to}`]);
      ws.addRow([]);
      const head = ws.addRow([
        "Name",
        "Role",
        "Open visits",
        "Site visits",
        "Calls",
        "Sales",
        "New contacts",
        "Days reported",
        "Added by manager",
        "Last activity",
      ]);
      head.font = { bold: true };
      t.members.forEach((m) =>
        ws.addRow([
          m.name + (m.isManager ? " (Manager)" : ""),
          m.role,
          m.openVisits,
          m.siteVisits,
          m.calls,
          m.sales,
          m.contactsAdded,
          m.daysReported,
          m.onBehalfEntries,
          fmt(m.lastActivity),
        ]),
      );
      const tot = ws.addRow([
        "TOTAL",
        "",
        t.totals.openVisits,
        t.totals.siteVisits,
        t.totals.calls,
        t.totals.sales,
        t.totals.contactsAdded,
      ]);
      tot.font = { bold: true };
      ws.addRow([]);
      ws.addRow(["Daily breakdown"]).font = { bold: true };
      ws.addRow([
        "Date",
        "Name",
        "Open visits",
        "Site visits",
        "Calls",
        "Sales",
        "Added by",
      ]).font = { bold: true };
      t.daily.forEach((r) =>
        ws.addRow([
          r.date,
          r.name,
          r.openVisits,
          r.siteVisits,
          r.calls,
          r.sales,
          r.addedBy || "",
        ]),
      );
      ws.columns.forEach((c) => (c.width = 18));
    });
    if (!teams.length) wb.addWorksheet("No data");
    const buf = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `team-real-estate-${data?.from || ""}-to-${data?.to || ""}.xlsx`,
    );
  };

  return (
    <>
      <PageTitle activeMenu="Team Combined Report" motherMenu="Workforce" />
      <div className="space-y-6">
        <section className={`${panel} p-6`}>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Team Real Estate — Combined Report
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Visits, calls and sales of every team member in one place.
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Team
                <select
                  className={`${inputClass} mt-1 block`}
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                >
                  <option value="">
                    {isSuperAdmin ? "All teams" : "All my teams"}
                  </option>
                  {(teamData?.teams || []).map((t) => (
                    <option key={t._id} value={t._id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                From
                <input
                  type="date"
                  className={`${inputClass} mt-1 block`}
                  value={from || data?.from || ""}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </label>
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                To
                <input
                  type="date"
                  className={`${inputClass} mt-1 block`}
                  value={to || data?.to || ""}
                  onChange={(e) => setTo(e.target.value)}
                />
              </label>
              {/* <button
                type="button"
                className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50"
                disabled={!teams.length}
                onClick={exportExcel}
              >
                Export Excel
              </button> */}
            </div>
          </div>
        </section>

        {isFetching && !teams.length && (
          <section className={`${panel} p-10 text-center text-gray-400`}>
            Loading…
          </section>
        )}
        {!isFetching && !teams.length && (
          <section className={`${panel} p-10 text-center text-gray-400`}>
            No team found.
          </section>
        )}

        {teams.map((t) => (
          <section key={t._id} className="space-y-3">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              <Kpi label="Open visits" value={t.totals.openVisits} />
              <Kpi label="Site visits" value={t.totals.siteVisits} />
              <Kpi label="Calls" value={t.totals.calls} />
              <Kpi label="Sales" value={t.totals.sales} />
              <Kpi label="New contacts" value={t.totals.contactsAdded} />
            </div>
            <div className={panel}>
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
                <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  {t.name} — Manager: {t.manager?.name || "—"}
                </div>
                <button
                  type="button"
                  className="text-xs font-medium text-amber-700 hover:underline"
                  onClick={() => setShowDaily((v) => !v)}
                >
                  {showDaily ? "Hide daily breakdown" : "Show daily breakdown"}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-2 text-left">Member</th>
                      <th className="px-4 py-2 text-center">Open visits</th>
                      <th className="px-4 py-2 text-center">Site visits</th>
                      <th className="px-4 py-2 text-center">Calls</th>
                      <th className="px-4 py-2 text-center">Sales</th>
                      <th className="px-4 py-2 text-center">New contacts</th>
                      <th className="px-4 py-2 text-center">Days reported</th>
                      <th className="px-4 py-2 text-center">
                        Added by manager
                      </th>
                      <th className="px-4 py-2 text-center">Last activity</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {t.members.map((m) => (
                      <tr key={m.employeeId} className="hover:bg-amber-50/30">
                        <td className="px-4 py-2 font-medium text-gray-800">
                          {m.name}
                          {m.isManager && (
                            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                              MANAGER
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center tabular-nums">
                          {m.openVisits}
                        </td>
                        <td className="px-4 py-2 text-center tabular-nums">
                          {m.siteVisits}
                        </td>
                        <td className="px-4 py-2 text-center tabular-nums">
                          {m.calls}
                        </td>
                        <td className="px-4 py-2 text-center tabular-nums">
                          {m.sales}
                        </td>
                        <td className="px-4 py-2 text-center tabular-nums">
                          {m.contactsAdded}
                        </td>
                        <td className="px-4 py-2 text-center tabular-nums">
                          {m.daysReported}
                        </td>
                        <td className="px-4 py-2 text-center tabular-nums">
                          {m.onBehalfEntries}
                        </td>
                        <td className="px-4 py-2 text-center text-gray-500">
                          {fmt(m.lastActivity)}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button
                            type="button"
                            className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 hover:border-amber-400"
                            onClick={() =>
                              navigate(
                                `/staff-reporting/employee/${m.employeeId}`,
                              )
                            }
                          >
                            Reports
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {showDaily && (
                <div className="overflow-x-auto border-t border-gray-100">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                        <th className="px-4 py-2 text-left">Date</th>
                        <th className="px-4 py-2 text-left">Member</th>
                        <th className="px-4 py-2 text-center">Open</th>
                        <th className="px-4 py-2 text-center">Site</th>
                        <th className="px-4 py-2 text-center">Calls</th>
                        <th className="px-4 py-2 text-center">Sales</th>
                        <th className="px-4 py-2 text-left">Added by</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {t.daily.length === 0 && (
                        <tr>
                          <td
                            colSpan={7}
                            className="px-4 py-6 text-center text-gray-400"
                          >
                            No daily activity in this period.
                          </td>
                        </tr>
                      )}
                      {t.daily.map((r) => (
                        <tr key={`${r.employeeId}-${r.date}`}>
                          <td className="px-4 py-2">{r.date}</td>
                          <td className="px-4 py-2">{r.name}</td>
                          <td className="px-4 py-2 text-center tabular-nums">
                            {r.openVisits}
                          </td>
                          <td className="px-4 py-2 text-center tabular-nums">
                            {r.siteVisits}
                          </td>
                          <td className="px-4 py-2 text-center tabular-nums">
                            {r.calls}
                          </td>
                          <td className="px-4 py-2 text-center tabular-nums">
                            {r.sales}
                          </td>
                          <td className="px-4 py-2 text-gray-500">
                            {r.addedBy || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        ))}
      </div>
    </>
  );
};

export default ClubReport;
